import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Popconfirm, Segmented, Space, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ListTree, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { menusApi } from '@api/menus';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { MenuFormModal } from '../components/MenuFormModal';
import {
  MENU_LOCATIONS,
  type MenuCreateInput,
  type MenuListItem,
  type MenuUpdateInput,
} from '../types';

const { Text } = Typography;

type LocationFilter = 'ALL' | (typeof MENU_LOCATIONS)[number];

export function MenusPage() {
  const { t } = useTranslation(['menus', 'common']);
  const td = t as unknown as (key: string) => string;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canManage = useCan(PERMISSIONS.MENU_MANAGE);

  const [languageId, setLanguageId] = useState<string | undefined>(undefined);
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MenuListItem | null>(null);

  const { data: languages = [] } = useActiveLanguages();
  const langCodeById = useMemo(
    () => new Map(languages.map((l) => [l.id, l.code])),
    [languages],
  );

  const { data: menus = [], isLoading } = useQuery({
    queryKey: ['menus', { languageId, locationFilter }],
    queryFn: () =>
      menusApi.list({
        languageId,
        location: locationFilter === 'ALL' ? undefined : locationFilter,
      }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['menus'] });

  const createMutation = useMutation({
    mutationFn: (input: MenuCreateInput) => menusApi.create(input),
    onSuccess: async () => {
      toast.success(t('toast.created'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: MenuUpdateInput }) =>
      menusApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('toast.updated'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (row: MenuListItem) => menusApi.setPublished(row.id, row.status !== 'Published'),
    onSuccess: async () => {
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => menusApi.remove(id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: MenuListItem) => {
    setEditing(row);
    setModalOpen(true);
  };

  const goToEditor = (row: MenuListItem) => {
    void navigate(ROUTES.MENU_EDIT.replace(':id', row.id));
  };

  const columns: ColumnsType<MenuListItem> = [
    {
      title: t('columns.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row) => (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => goToEditor(row)}>
          {name}
        </Button>
      ),
    },
    {
      title: t('columns.location'),
      dataIndex: 'location',
      key: 'location',
      render: (loc: string) => <Tag>{loc ? td(`location.${loc}`) : '—'}</Tag>,
    },
    {
      title: t('columns.language'),
      key: 'language',
      render: (_, r) =>
        r.languageId ? <Tag color="blue">{langCodeById.get(r.languageId) ?? r.languageId.slice(0, 8)}</Tag> : null,
    },
    {
      title: t('columns.itemCount'),
      dataIndex: 'itemCount',
      key: 'itemCount',
      align: 'right',
      render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>,
    },
    {
      title: t('columns.status'),
      key: 'status',
      render: (_, r) => (
        <Space size={8}>
          <Switch
            size="small"
            checked={r.status === 'Published'}
            disabled={!canManage || publishMutation.isPending}
            onChange={() => publishMutation.mutate(r)}
            aria-label={t('columns.status')}
          />
          <Tag color={r.status === 'Published' ? 'green' : 'default'}>
            {r.status === 'Published' ? t('status.published') : t('status.draft')}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<ListTree size={15} />}
            onClick={() => goToEditor(r)}
            aria-label={t('actions.editItems')}
            title={t('actions.editItems')}
          />
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => openEdit(r)}
            disabled={!canManage}
            aria-label={t('form.editTitle')}
          />
          <Popconfirm
            title={t('delete.title')}
            description={t('delete.confirm', { name: r.name })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(r.id)}
            disabled={!canManage}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={15} />}
              disabled={!canManage}
              loading={deleteMutation.isPending && deleteMutation.variables === r.id}
              aria-label={t('delete.title')}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('title') }]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={openCreate}>
              {t('new')}
            </Button>
          ) : null
        }
      />

      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Segmented<LocationFilter>
              size="small"
              value={locationFilter}
              onChange={setLocationFilter}
              options={[
                { value: 'ALL', label: t('common:labels.all') },
                ...MENU_LOCATIONS.map((loc) => ({ value: loc, label: td(`location.${loc}`) })),
              ]}
            />
            <LanguagePicker
              value={languageId}
              onChange={setLanguageId}
              allowClear
              placeholder={t('common:contentLanguage.filterAll')}
              style={{ minWidth: 180 }}
            />
          </div>

          <Table<MenuListItem>
            rowKey="id"
            columns={columns}
            dataSource={menus}
            loading={isLoading}
            pagination={false}
            locale={{ emptyText: <Text type="secondary">{t('empty')}</Text> }}
          />
        </Space>
      </Card>

      <MenuFormModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, input) => updateMutation.mutate({ id, input })}
      />
    </div>
  );
}
